import { lireTout } from '@/lib/pagination';
import { supabase } from '@/lib/supabase';
import type { TauxTva } from '@/modules/catalogue/api';

export const ORG_ID = '00000000-0000-0000-0000-000000000001';

export const DEVIS_STATUT_LABELS: Record<string, string> = {
  brouillon: 'Brouillon',
  a_envoyer: 'À envoyer',
  envoye: 'Envoyé',
  consulte: 'Consulté',
  signe: 'Signé',
  refuse: 'Refusé',
  expire: 'Expiré',
  annule: 'Annulé',
  converti_facture: 'Facturé',
};

export interface DevisListItem {
  id: string;
  numero: string;
  statut: string;
  objet: string;
  date_emission: string;
  montant_ttc: string;
  contact_id: string;
}

export interface DevisLigneRow {
  article_id: string | null;
  libelle: string;
  /** Le détail de la prestation, sous la désignation. Il s'imprime sur le devis. */
  description: string | null;
  quantite: string;
  unite_id: string | null;
  prix_unitaire_ht: string;
  remise_pourcentage: string;
  taux_tva_id: string | null;
  total_ht: string;
  total_ttc: string;
  ordre: number;
}

export interface DevisDetail {
  id: string;
  numero: string;
  statut: string;
  contact_id: string;
  chantier_id: string | null;
  objet: string;
  date_emission: string;
  date_expiration: string;
  devis_lignes: DevisLigneRow[];
}

function num(value: string | number): number {
  const n = Number(String(value).replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
}

export interface FormLigne {
  article_id: string;
  libelle: string;
  description: string;
  quantite: string;
  unite_id: string;
  prix_unitaire_ht: string;
  remise_pourcentage: string;
  taux_tva_id: string;
}

export interface DetailTva {
  tauxCode: string;
  tauxValeur: number;
  baseHt: number;
  montantTva: number;
}

export interface DevisTotals {
  montant_ht: number;
  montant_tva: number;
  montant_ttc: number;
  detail_tva: DetailTva[];
  ligneTotals: { total_ht: number; total_ttc: number }[];
}

// Calcule les totaux d'un devis : par ligne (HT, TTC) et globaux (HT, TVA détaillée, TTC).
export function computeDevisTotals(lignes: FormLigne[], tva: TauxTva[]): DevisTotals {
  const byId = new Map(tva.map((t) => [t.id, t]));
  let montant_ht = 0;
  let montant_ttc = 0;
  const detailMap = new Map<string, DetailTva>();
  const ligneTotals = lignes.map((l) => {
    const ht = num(l.quantite) * num(l.prix_unitaire_ht) * (1 - num(l.remise_pourcentage) / 100);
    const t = l.taux_tva_id ? byId.get(l.taux_tva_id) : undefined;
    const taux = t ? num(t.taux) : 0;
    const tvaMontant = ht * taux;
    montant_ht += ht;
    montant_ttc += ht + tvaMontant;
    const key = t?.id ?? 'aucun';
    const cur = detailMap.get(key) ?? {
      tauxCode: t?.code ?? '0',
      tauxValeur: taux,
      baseHt: 0,
      montantTva: 0,
    };
    cur.baseHt += ht;
    cur.montantTva += tvaMontant;
    detailMap.set(key, cur);
    return { total_ht: ht, total_ttc: ht + tvaMontant };
  });
  return {
    montant_ht,
    montant_tva: montant_ttc - montant_ht,
    montant_ttc,
    detail_tva: [...detailMap.values()],
    ligneTotals,
  };
}

export async function listDevis(): Promise<DevisListItem[]> {
  // Par tranches : PostgREST plafonne à 1 000 lignes sans le dire. C'est ce qui
  // avait tronqué la liste des clients ; la liste des devis y viendra.
  return lireTout<DevisListItem>((de, a) =>
    supabase
      .from('devis')
      .select('id,numero,statut,objet,date_emission,montant_ttc,contact_id')
      .order('date_emission', { ascending: false })
      .order('id')
      .range(de, a)
      .overrideTypes<DevisListItem[], { merge: false }>(),
  );
}

export async function getDevis(id: string): Promise<DevisDetail> {
  const { data, error } = await supabase
    .from('devis')
    .select(
      'id,numero,statut,contact_id,chantier_id,objet,date_emission,date_expiration,devis_lignes(article_id,libelle,description,quantite,unite_id,prix_unitaire_ht,remise_pourcentage,taux_tva_id,total_ht,total_ttc,ordre)',
    )
    .eq('id', id)
    .single();
  if (error) throw new Error(error.message);
  return data as unknown as DevisDetail;
}

// Numérotation applicative (MVP). En Phase 4, numérotation atomique NF525 via RPC.
export async function nextDevisNumber(): Promise<string> {
  const year = new Date().getFullYear();
  const { count, error } = await supabase
    .from('devis')
    .select('id', { count: 'exact', head: true })
    .like('numero', `DEV-${String(year)}-%`);
  if (error) throw new Error(error.message);
  return `DEV-${String(year)}-${String((count ?? 0) + 1).padStart(4, '0')}`;
}

export interface DevisHeaderInput {
  contact_id: string;
  /** Le chantier auquel rattacher le chiffre d'affaires. */
  chantier_id: string | null;
  objet: string;
  date_emission: string;
  date_expiration: string;
  statut: string;
  montant_ht: string;
  montant_tva: string;
  montant_ttc: string;
  detail_tva: DetailTva[];
}

export interface LigneInsert {
  ordre: number;
  type: string;
  article_id: string | null;
  libelle: string;
  description: string | null;
  quantite: string;
  unite_id: string | null;
  prix_unitaire_ht: string;
  remise_pourcentage: string;
  taux_tva_id: string | null;
  total_ht: string;
  total_ttc: string;
}

async function replaceLignes(devisId: string, lignes: LigneInsert[]): Promise<void> {
  const del = await supabase.from('devis_lignes').delete().eq('devis_id', devisId);
  if (del.error) throw new Error(del.error.message);
  if (lignes.length > 0) {
    const ins = await supabase
      .from('devis_lignes')
      .insert(lignes.map((l) => ({ ...l, devis_id: devisId, organization_id: ORG_ID })));
    if (ins.error) throw new Error(ins.error.message);
  }
}

export async function createDevis(
  header: DevisHeaderInput,
  lignes: LigneInsert[],
  numero: string,
  extra?: Record<string, unknown>,
): Promise<string> {
  const { data, error } = await supabase
    .from('devis')
    .insert({ ...header, ...extra, numero, organization_id: ORG_ID })
    .select('id')
    .single();
  if (error) throw new Error(error.message);
  const devisId = (data as { id: string }).id;
  await replaceLignes(devisId, lignes);
  return devisId;
}

export async function updateDevis(
  id: string,
  header: DevisHeaderInput,
  lignes: LigneInsert[],
): Promise<void> {
  const { error } = await supabase.from('devis').update(header).eq('id', id);
  if (error) throw new Error(error.message);
  await replaceLignes(id, lignes);
}

// ----- Chargement complet pour le rendu PDF ---------------------------------

export interface DevisPdfAdresse {
  usage: string;
  libelle: string | null;
  ligne1: string;
  ligne2: string | null;
  code_postal: string;
  ville: string;
  est_default: boolean;
}
export interface DevisPdfContact {
  type_personne: string;
  civilite: string | null;
  nom: string | null;
  prenom: string | null;
  raison_sociale: string | null;
  email: string | null;
  telephone: string | null;
  telephone_mobile: string | null;
  siret: string | null;
  adresses: DevisPdfAdresse[];
}
export interface DevisPdfLigne {
  id: string;
  libelle: string;
  description: string | null;
  quantite: string;
  prix_unitaire_ht: string;
  remise_pourcentage: string;
  total_ht: string;
  total_ttc: string;
  ordre: number;
  unites: { code: string } | null;
  taux_tva: { taux: string; code: string } | null;
}
/** Le chantier du devis : son adresse est CELLE DES TRAVAUX. */
export interface DevisPdfChantier {
  libelle: string;
  adresse_ligne1: string | null;
  adresse_ligne2: string | null;
  code_postal: string | null;
  ville: string | null;
}

export interface DevisPdf {
  id: string;
  numero: string;
  contact_id: string;
  chantiers: DevisPdfChantier | null;
  objet: string;
  statut: string;
  date_emission: string;
  date_expiration: string;
  montant_ht: string;
  montant_tva: string;
  montant_ttc: string;
  detail_tva: { tauxCode: string; tauxValeur: number; baseHt: number; montantTva: number }[] | null;
  contacts: DevisPdfContact | null;
  devis_lignes: DevisPdfLigne[];
}

export async function getDevisForPdf(id: string): Promise<DevisPdf> {
  const { data, error } = await supabase
    .from('devis')
    .select(
      'id,numero,contact_id,objet,statut,date_emission,date_expiration,montant_ht,montant_tva,montant_ttc,detail_tva,chantiers(libelle,adresse_ligne1,adresse_ligne2,code_postal,ville),contacts(type_personne,civilite,nom,prenom,raison_sociale,email,telephone,telephone_mobile,siret,adresses(usage,libelle,ligne1,ligne2,code_postal,ville,est_default)),devis_lignes(id,libelle,description,quantite,prix_unitaire_ht,remise_pourcentage,total_ht,total_ttc,ordre,unites(code),taux_tva(taux,code))',
    )
    .eq('id', id)
    .single();
  if (error) throw new Error(error.message);
  const d = data as unknown as DevisPdf;
  d.devis_lignes.sort((a, b) => a.ordre - b.ordre);
  return d;
}

// ----- Cycle de vie (pilote le centre d'alertes) ----------------------------

export async function setDevisStatut(id: string, statut: string): Promise<void> {
  const patch: Record<string, unknown> = { statut };
  if (statut === 'envoye') patch.envoye_at = new Date().toISOString();
  const { error } = await supabase.from('devis').update(patch).eq('id', id);
  if (error) throw new Error(error.message);
}

/**
 * Annule un « Accepté » ou un « Refusé » cliqué par erreur : le devis revient
 * à « Envoyé ». Refusé si une facture (ou un acompte) a déjà été faite sur ce
 * devis : il faut d'abord la supprimer (brouillon) ou l'annuler par un avoir,
 * sinon le CRM afficherait de l'argent facturé sur un devis non signé.
 */
export async function annulerDecisionDevis(id: string): Promise<void> {
  const { data: factures, error: errF } = await supabase
    .from('factures')
    .select('numero,statut')
    .eq('devis_id', id)
    .neq('statut', 'annulee_avoir')
    .is('deleted_at', null)
    .overrideTypes<{ numero: string | null; statut: string }[], { merge: false }>();
  if (errF) throw new Error(errF.message);
  if (factures.length > 0) {
    const liste = factures
      .map((f) =>
        f.statut === 'brouillon' ? 'un brouillon de facture' : `la facture ${f.numero ?? ''}`,
      )
      .join(', ');
    throw new Error(
      `Impossible : ce devis a déjà ${liste}. Supprimez le brouillon ou annulez la facture par un avoir, puis recommencez.`,
    );
  }
  const { data, error } = await supabase
    .from('devis')
    .update({ statut: 'envoye' })
    .eq('id', id)
    .in('statut', ['signe', 'refuse'])
    .select('id');
  if (error) throw new Error(error.message);
  if (data.length === 0) throw new Error('Ce devis n’est plus accepté ni refusé : rien à annuler.');
}

export async function markDevisRelance(id: string): Promise<void> {
  // Lit le metadata existant pour le FUSIONNER : un update direct remplacerait tout
  // l'objet et détruirait les autres clés déjà stockées.
  const { data, error: readErr } = await supabase
    .from('devis')
    .select('metadata')
    .eq('id', id)
    .single();
  if (readErr) throw new Error(readErr.message);
  const metadata = {
    ...((data.metadata as Record<string, unknown> | null) ?? {}),
    derniere_relance_at: new Date().toISOString(),
  };
  const { error } = await supabase.from('devis').update({ metadata }).eq('id', id);
  if (error) throw new Error(error.message);
}
